<template>
  <div id="copy-order" class="order-steps-container">
    <arch
      @go-to-step="goToStep"
      :titles="steps"
      :activeStep="activeStep"
      :errors="errors"
      @submit-order="sendOrder"
    ></arch>
    <section>
      <router-view></router-view>
    </section>
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useShopsStore } from '@/stores/Shops.store'
import { useCopyOrderStore } from '@/features/copyOrder/CopyOrder.store'
import { EventBus } from '@/eventbus/EventBus'

import Arch from '@/components/Arch.vue'

import { saveCopyOrder } from './CopyOrderApi'

import isEmpty from 'lodash-es/isEmpty'
import OrderMixin from '@/mixins/OrderMixin'

import SendingOrder from '@/dialogs/SendingOrder.vue'
import ErrorMessage from '@/dialogs/ErrorMessage.vue'
import OrderConfirm from '@/dialogs/OrderConfirm.vue'

export default {
  data() {
    return {
      step: 'step1',
      steps: ['Ophalen', 'Opleveren', 'Factureren', 'Controleren'],
      inProgress: false
    }
  },
  mixins: [OrderMixin],
  components: {
    Arch
  },
  computed: {
    ...mapStores(useCopyOrderStore),
    copyOrderStore() {
      return useCopyOrderStore()
    },
    shopsStore() {
      return useShopsStore()
    },
    shops() {
      return this.shopsStore.shopsList
    },
    shop() {
      return this.copyOrderStore.shop
    },
    orderDetails() {
      return this.copyOrderStore.orderDetails
    },
    errors() {
      return this.copyOrderStore.orderErrors.length
    },
    activeStep() {
      if (this.$route.name === 'copyOrderPickUp') {
        return 'step1'
      } else if (this.$route.name === 'copyOrderDeliver') {
        return 'step2'
      } else if (this.$route.name === 'copyOrderInvoice') {
        return 'step3'
      } else if (this.$route.name === 'copyOrderConfirm') {
        return 'step4'
      }
    },
    customerNumber() {
      return this.copyOrderStore.orderDetails.invoiceAddress.newCustomer
        ? 0
        : this.copyOrderStore.orderDetails.invoiceAddress.customerNumber || 0
    }
  },
  methods: {
    goToStep(step) {
      switch (step) {
        case 'step1':
          this.$router.push({
            name: 'copyOrderPickUp'
          })
          break
        case 'step2':
          this.$router.push({
            name: 'copyOrderDeliver'
          })
          break
        case 'step3':
          this.$router.push({
            name: 'copyOrderInvoice'
          })
          break
        case 'step4':
          this.$router.push({
            name: 'copyOrderConfirm'
          })
          break
      }
    },
    updateOrder(key, val) {
      this.copyOrderStore.updateOrderDetails(key, val)
    },
    sendOrder() {
      if (this.inProgress) return
      this.inProgress = true
      const order = this.prepareOrder(this.orderDetails)

      if (!order) {
        this.inProgress = false
        return
      }

      this.$modal.show(SendingOrder, null, {
        height: 120,
        width: 280,
        adaptive: true,
        name: 'sending',
        clickToClose: false
      })

      const isDummy = this.rootStore.theme?.customFields?.checkOut?.dummyOrder

      const orderRequest = isDummy
        ? new Promise((resolve) =>
            setTimeout(() => resolve({ dummy: true }), 800)
          )
        : saveCopyOrder(order)

      orderRequest.then(
        (resp) => {
          this.inProgress = false
          this.$modal.hideAll()

          this.copyOrderStore.resetOrder()
          this.$modal.show(
            OrderConfirm,
            {},
            {
              height: 'auto',
              width: 360,
              adaptive: true
            }
          )
        },
        (resp) => {
          this.$modal.hide('sending')
          this.inProgress = false
          this.$modal.show(
            ErrorMessage,
            {
              title: 'Probleem bij het plaatsen van uw order',
              text: resp.message
            },
            {
              height: 250,
              width: 380,
              adaptive: true
            }
          )
        }
      )
    }
  },
  async created() {
    if (isEmpty(this.orderDetails.pickUpAddress)) {
      const pickUpAddress = this.setDeliveryAddress()

      this.copyOrderStore.updateOrderDetails('pickUpAddress', pickUpAddress)

      this.copyOrderStore.updateOrderDetails('deliveryAddress', pickUpAddress)
    }

    if (isEmpty(this.orderDetails.invoiceAddress)) {
      const invoiceDetails = this.getDefaultInvoiceDetails()

      this.copyOrderStore.updateOrderDetails(
        'invoiceAddress',
        invoiceDetails.address
      )

      this.copyOrderStore.updateProject(invoiceDetails.project)

      this.copyOrderStore.updateOrderDetails(
        'reference',
        invoiceDetails.reference
      )
    }

    await this.copyOrderStore.getTheShop({
      deliveryAddress: this.orderDetails.deliveryAddress,
      invoiceId: this.customerNumber
    })

    EventBus.on('submitOrder', this.sendOrder)
  },
  unmounted() {
    EventBus.off('submitOrder', this.sendOrder)
  },
  watch: {
    'orderDetails.pickUpAddress': {
      async handler() {
        await this.copyOrderStore.getTheShop({
          deliveryAddress: this.orderDetails.pickUpAddress,
          invoiceId: this.customerNumber
        })
      },
      deep: true
    },
    'orderDetails.invoiceAddress': {
      async handler() {
        await this.copyOrderStore.getTheShop({
          deliveryAddress: this.orderDetails.pickUpAddress,
          invoiceId: this.customerNumber
        })
      },
      deep: true
    },
    shop: {
      async handler(val) {
        const dates = await this.copyOrderStore.getThePickUpDates({
          shopId: this.shop.id,
          invoiceId: this.orderDetails.invoiceAddress.id
        })
        if (isEmpty(this.orderDetails.pickUpDate)) {
          let puDate = {}
          puDate = dates.find((date) => {
            return date.active
          })

          puDate.time = puDate.times[0].time

          this.copyOrderStore.updateOrderDetails('pickUpDate', puDate)
        }
      },
      deep: true
    },
    'orderDetails.pickUpDate.value': {
      async handler(val) {
        if (val === 'spoed') {
          return
        }

        const dates = await this.copyOrderStore.getTheDeliveryDates({
          shopId: this.shop.id,
          invoiceId: this.customerNumber,
          secure: false,
          offset: {
            date: this.orderDetails.pickUpDate.value,
            time: this.orderDetails.pickUpDate.time
          }
        })

        let delDate = {}
        delDate = dates.find((date) => {
          return date.active
        })

        delDate.time = delDate.times[0].time

        this.copyOrderStore.updateOrderDetails('deliveryDate', delDate)
      },
      deep: true
    }
  }
}
</script>
<style lang="scss"></style>
